import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  ButtonGroup,
  Paper,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import axios from '../services/api.js';
import styles from '../styles/card.module.css';
import DirectionToggle from './DirectionToggle';
import {
  DIFFICULTIES,
  DIFFICULTY_WEIGHTS,
  DIFFICULTY_COLORS_MUI,
} from '../constants/difficulties';
import UserInfo from './UserInfo';

function PlayDeck() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isLeftSide, setIsLeftSide] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsWithDifficulty, setRowsWithDifficulty] = useState([]);
  const [direction, setDirection] = useState('both');
  const [skipEasy, setSkipEasy] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchDeckAndSettings();
  }, [deckId]);

  const fetchDeckAndSettings = async () => {
    try {
      const [deckResponse, difficultiesResponse, settingsResponse] =
        await Promise.all([
          axios.get(`/decks/${deckId}`),
          axios.get(`/decks/${deckId}/difficulties`),
          axios.get(`/decks/${deckId}/settings`),
        ]);

      const deckData = deckResponse.data;
      const difficulties = difficultiesResponse.data;
      const settings = settingsResponse.data;

      // Combine deck data with difficulties
      const rowsWithDiff = deckData.data.map((row, index) => ({
        ...row,
        difficulty: difficulties[index]?.difficulty || 'normal',
      }));

      setDeck(deckData);
      setRowsWithDifficulty(rowsWithDiff);
      setDirection(settings.direction);
      setSkipEasy(!!settings.skip_easy);

      // Remove the initial side setting and pickNewRow call from here
      // Let pickNewRow handle everything
      pickNewRow(deckData, rowsWithDiff, settings.skip_easy);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching deck:', error);
      alert('Failed to load deck');
    }
  };

  // Move side selection logic to a separate function
  const determineCardSide = (selectedRow, currentDirection, headers) => {
    // First check if one side is empty
    const leftContent = selectedRow[headers[0]];
    const rightContent = selectedRow[headers[1]];

    if (!leftContent && rightContent) {
      return false; // Show right side
    }
    if (!rightContent && leftContent) {
      return true; // Show left side
    }

    // If both sides have content, use direction setting
    switch (currentDirection) {
      case 'leftToRight':
        return true;
      case 'rightToLeft':
        return false;
      case 'both':
      default:
        return Math.random() < 0.5;
    }
  };

  const pickNewRow = (
    deckData,
    rows = rowsWithDifficulty,
    skipEasyCards = skipEasy
  ) => {
    if (!deckData || !rows || rows.length === 0) return;

    // Filter out easy cards if skipEasyCards is true
    const availableRows = skipEasyCards
      ? rows.filter((row) => row.difficulty !== DIFFICULTIES.EASY)
      : rows;

    if (availableRows.length === 0) {
      // Check if there are any cards at all
      if (rows.length === 0) {
        alert('No cards available in this deck');
        navigate('/');
        return;
      }

      // If we have cards but they're all marked as easy and we're skipping easy cards
      if (
        skipEasyCards &&
        rows.some((row) => row.difficulty === DIFFICULTIES.EASY)
      ) {
        setOpenDialog(true);
        return;
      }

      alert('No cards available with current settings');
      return;
    }

    // Calculate weighted probabilities
    const totalWeight = availableRows.reduce(
      (sum, row) => sum + DIFFICULTY_WEIGHTS[row.difficulty || 'unreported'],
      0
    );

    let random = Math.random() * totalWeight;
    let selectedRow;

    for (const row of availableRows) {
      random -= DIFFICULTY_WEIGHTS[row.difficulty || 'unreported'];
      if (random <= 0) {
        selectedRow = row;
        break;
      }
    }

    // Set both the row and side in one update
    setCurrentRow(selectedRow);
    setIsLeftSide(determineCardSide(selectedRow, direction, deckData.headers));
    setShowAnswer(false);
  };

  const handleDifficultyClick = async (difficulty) => {
    try {
      // Save difficulty to database
      await axios.post(`/decks/${deckId}/difficulties`, {
        rowIndex: rowsWithDifficulty.indexOf(currentRow),
        difficulty,
      });

      // Update local state
      const updatedRows = rowsWithDifficulty.map((row) =>
        row === currentRow ? { ...row, difficulty } : row
      );
      setRowsWithDifficulty(updatedRows);

      // Pick next card with updated difficulties
      pickNewRow(deck, updatedRows);
    } catch (error) {
      console.error('Error saving difficulty:', error);
      alert('Failed to save difficulty rating');
    }
  };

  const handleShowClick = () => {
    setShowAnswer(true);
  };

  // Keyboard shortcuts: 1=Easy, 2=Normal, 3=Challenging, 4=Hard (only when answer is shown)
  useEffect(() => {
    if (!showAnswer || openDialog || !currentRow) return;

    const keyToDifficulty = {
      '1': DIFFICULTIES.EASY,
      '2': DIFFICULTIES.NORMAL,
      '3': DIFFICULTIES.CHALLENGING,
      '4': DIFFICULTIES.HARD,
    };

    const handleKeyDown = (event) => {
      const key = event.key;
      if (!(key in keyToDifficulty)) return;
      // Don't trigger when typing in an input
      const tag = event.target?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;

      event.preventDefault();
      handleDifficultyClick(keyToDifficulty[key]);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAnswer, openDialog, currentRow]);

  // Keyboard shortcut: Space = Show (when Show button is visible)
  // Use capture phase so we handle Space before it reaches the "Skip easy cards" checkbox.
  // If focus is on the checkbox (or its label), let Space toggle it; otherwise Space triggers Show.
  useEffect(() => {
    if (showAnswer || openDialog || !currentRow) return;

    const handleKeyDown = (event) => {
      if (event.key !== ' ') return;
      // Let Space toggle the checkbox when the user explicitly focused it
      if (event.target.closest?.('[data-skip-easy-control]')) return;
      const tag = event.target?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;

      event.preventDefault();
      event.stopPropagation();
      handleShowClick();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [showAnswer, openDialog, currentRow]);

  const handleDirectionChange = async (newDirection) => {
    setDirection(newDirection);
    setShowAnswer(false); // Reset card when direction changes

    try {
      await axios.put(`/decks/${deckId}/settings`, { direction: newDirection });
    } catch (error) {
      console.error('Error saving direction:', error);
      // Continue with local state even if save fails
    }
  };

  const handleSkipEasyChange = async (event) => {
    const newSkipEasy = event.target.checked;
    setSkipEasy(newSkipEasy);

    try {
      await axios.put(`/decks/${deckId}/settings`, {
        direction,
        skip_easy: newSkipEasy,
      });
      pickNewRow(deck, rowsWithDifficulty, newSkipEasy);
    } catch (error) {
      console.error('Error saving settings:', error);
      // Continue with local state even if save fails
    }
  };

  const handleDisableSkipEasy = async () => {
    setSkipEasy(false);
    setOpenDialog(false);

    try {
      await axios.put(`/decks/${deckId}/settings`, {
        direction,
        skip_easy: false,
      });
      // Pick a new row with skip_easy set to false
      pickNewRow(deck, rowsWithDifficulty, false);
    } catch (error) {
      console.error('Error saving settings:', error);
      // Continue with local state even if save fails
      pickNewRow(deck, rowsWithDifficulty, false);
    }
  };

  // Modify the conditional rendering logic to handle the case when all cards are marked as easy
  if (isLoading) {
    return (
      <Box
        className={styles.cardContainer}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Show the dialog directly if it's open, regardless of other conditions
  if (openDialog) {
    return (
      <Box className={styles.cardContainer}>
        <UserInfo />
        <Typography
          variant='h5'
          component='h1'
          sx={{
            textAlign: 'center',
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 'bold',
          }}
        >
          {deck.name}
        </Typography>

        <Dialog
          open={openDialog}
          onClose={() => navigate('/')}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle id='alert-dialog-title'>
            {'All cards are marked as easy'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              All cards in this deck are marked as easy, and you have the "Skip
              easy cards" option enabled. Would you like to disable the "Skip
              easy cards" option to continue studying?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => navigate('/')} color='primary'>
              Return to Decks
            </Button>
            <Button onClick={handleDisableSkipEasy} color='primary' autoFocus>
              Disable "Skip easy cards"
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // If we have a deck but no current row, it means we couldn't find any cards to show
  if (!deck) {
    return (
      <Box
        className={styles.cardContainer}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography>Error loading deck</Typography>
      </Box>
    );
  }

  if (!currentRow || isLeftSide === null) {
    return (
      <Box
        className={styles.cardContainer}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography>No cards available with current settings</Typography>
        <Button variant='contained' onClick={() => navigate('/')}>
          Return to Decks
        </Button>
      </Box>
    );
  }

  const leftSideContent = deck.headers[0];
  const rightSideContent = deck.headers[1];
  const displayedContent = isLeftSide ? leftSideContent : rightSideContent;
  const hiddenContent = isLeftSide ? rightSideContent : leftSideContent;

  return (
    <Box className={styles.cardContainer}>
      <UserInfo />
      <Typography
        variant='h5'
        component='h1'
        sx={{
          textAlign: 'center',
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          fontWeight: 'bold',
        }}
      >
        {deck.name}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>{deck.headers[0]}</Typography>
          <DirectionToggle
            direction={direction}
            onDirectionChange={handleDirectionChange}
          />
          <Typography>{deck.headers[1]}</Typography>
        </Box>

        <FormControlLabel
          data-skip-easy-control
          control={
            <Checkbox
              checked={!!skipEasy}
              onChange={handleSkipEasyChange}
              color='primary'
              size='small'
            />
          }
          label={<Typography variant='body1'>Skip easy cards</Typography>}
        />
      </Box>

      <Paper
        elevation={3}
        className={styles.card}
        sx={{
          wordBreak: 'break-word',
        }}
      >
        {currentRow && (
          <Typography variant='h5'>
            {showAnswer
              ? currentRow[hiddenContent]
              : currentRow[displayedContent]}
          </Typography>
        )}
      </Paper>

      {!showAnswer ? (
        <Button
          variant='contained'
          onClick={handleShowClick}
          size='large'
          fullWidth={window.innerWidth < 600}
        >
          Show
        </Button>
      ) : (
        <ButtonGroup
          variant='contained'
          size='large'
          orientation={window.innerWidth < 600 ? 'vertical' : 'horizontal'}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            onClick={() => handleDifficultyClick(DIFFICULTIES.EASY)}
            color={DIFFICULTY_COLORS_MUI[DIFFICULTIES.EASY]}
          >
            Easy
          </Button>
          <Button
            onClick={() => handleDifficultyClick(DIFFICULTIES.NORMAL)}
            color={DIFFICULTY_COLORS_MUI[DIFFICULTIES.NORMAL]}
          >
            Normal
          </Button>
          <Button
            onClick={() => handleDifficultyClick(DIFFICULTIES.CHALLENGING)}
            color={DIFFICULTY_COLORS_MUI[DIFFICULTIES.CHALLENGING]}
          >
            Challenging
          </Button>
          <Button
            onClick={() => handleDifficultyClick(DIFFICULTIES.HARD)}
            color={DIFFICULTY_COLORS_MUI[DIFFICULTIES.HARD]}
          >
            Hard
          </Button>
        </ButtonGroup>
      )}

      <Button
        variant='outlined'
        onClick={() => navigate('/')}
        sx={{ mt: 2 }}
        fullWidth={window.innerWidth < 600}
      >
        Back to Decks
      </Button>
    </Box>
  );
}

export default PlayDeck;
